import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Plus, Trash2, Calculator } from 'lucide-react';
import { InvoiceLineItem } from '@/types/invoice';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxLine, formatTaxSuffix, netFromGross, taxFromNet, type TaxConfig } from '@/utils/tax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { AddCatalogPartButton } from '@/components/documents/AddCatalogPartButton';
import { AddCatalogServiceButton } from '@/components/documents/AddCatalogServiceButton';

interface InvoiceLineItemsProps {
  items: InvoiceLineItem[];
  onChange: (items: InvoiceLineItem[]) => void;
  readOnly?: boolean;
  /** Per-row snapshot config (preferred for existing invoices). Falls back to live company config. */
  taxConfig?: TaxConfig;
}

export function InvoiceLineItems({
  items,
  onChange,
  readOnly = false,
  taxConfig,
}: InvoiceLineItemsProps) {
  const liveTax = useCompanyTax();
  const tax = taxConfig ?? liveTax;
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkError, setBulkError] = useState('');
  const { formatMoney: formatCurrency } = useCompanyLocale();

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const gstAmount = taxFromNet(subtotal, tax);
  const total = subtotal + gstAmount;

  const handleAddItem = () => {
    onChange([
      ...items,
      { description: '', quantity: 1, unit_price: 0, amount: 0 },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  // Techs often quote one tax-inclusive price for the whole job instead of a
  // per-line breakdown. This puts that total on the first line (minus any lines
  // already priced, e.g. parts) so the invoice total matches it exactly.
  const handleApplyBulkPrice = () => {
    const gross = parseFloat(bulkPrice);
    if (!gross || gross <= 0) {
      setBulkError('Enter the total price first');
      return;
    }

    const targetSubtotal = netFromGross(gross, tax);
    const rows = items.length > 0
      ? [...items]
      : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }];

    const othersSum = rows.slice(1).reduce((sum, item) => sum + item.amount, 0);
    const firstAmount = targetSubtotal - othersSum;
    if (firstAmount < 0) {
      setBulkError('The other lines already add up to more than this total');
      return;
    }

    const first = { ...rows[0] };
    const quantity = first.quantity > 0 ? first.quantity : 1;
    first.quantity = quantity;
    first.unit_price = Math.round((firstAmount / quantity) * 100) / 100;
    first.amount = Math.round(quantity * first.unit_price * 100) / 100;
    rows[0] = first;

    setBulkError('');
    setBulkPrice('');
    onChange(rows);
  };

  const handleItemChange = (index: number, field: keyof InvoiceLineItem, value: string | number) => {
    const newItems = [...items];
    const item = { ...newItems[index] };

    if (field === 'description') {
      item.description = value as string;
    } else if (field === 'quantity') {
      item.quantity = Number(value) || 0;
      item.amount = Math.round(item.quantity * item.unit_price * 100) / 100;
    } else if (field === 'unit_price') {
      item.unit_price = Number(value) || 0;
      item.amount = Math.round(item.quantity * item.unit_price * 100) / 100;
    }

    newItems[index] = item;
    onChange(newItems);
  };

  if (readOnly) {
    return (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50%]">Description</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead className="text-right">Unit Price</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.description}</TableCell>
                <TableCell className="text-center">{item.quantity}</TableCell>
                <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-medium">Subtotal</TableCell>
              <TableCell className="text-right">{formatCurrency(subtotal)}</TableCell>
            </TableRow>
            {tax.enabled && (
              <TableRow>
                <TableCell colSpan={3} className="text-right font-medium">{formatTaxLine(tax)}</TableCell>
                <TableCell className="text-right">{formatCurrency(gstAmount)}</TableCell>
              </TableRow>
            )}
            <TableRow className="bg-muted/50">
              <TableCell colSpan={3} className="text-right font-bold text-lg">Total</TableCell>
              <TableCell className="text-right font-bold text-lg">{formatCurrency(total)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[45%]">Description</TableHead>
              <TableHead className="w-[12%] text-center">Qty</TableHead>
              <TableHead className="w-[18%] text-right">Unit Price</TableHead>
              <TableHead className="w-[18%] text-right">Amount</TableHead>
              <TableHead className="w-[7%]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="p-2">
                  <Input
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Enter description"
                    className="h-9"
                  />
                </TableCell>
                <TableCell className="p-2">
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    min={1}
                    className="h-9 text-center"
                  />
                </TableCell>
                <TableCell className="p-2">
                  <Input
                    type="number"
                    value={item.unit_price || ''}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    className="h-9 text-right"
                  />
                </TableCell>
                <TableCell className="p-2 text-right font-medium">
                  {formatCurrency(item.amount)}
                </TableCell>
                <TableCell className="p-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(index)}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-medium">Subtotal</TableCell>
              <TableCell className="text-right">{formatCurrency(subtotal)}</TableCell>
              <TableCell></TableCell>
            </TableRow>
            {tax.enabled && (
              <TableRow>
                <TableCell colSpan={3} className="text-right font-medium">{formatTaxLine(tax)}</TableCell>
                <TableCell className="text-right">{formatCurrency(gstAmount)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
            <TableRow className="bg-muted/50">
              <TableCell colSpan={3} className="text-right font-bold text-lg">Total</TableCell>
              <TableCell className="text-right font-bold text-lg">{formatCurrency(total)}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={handleAddItem} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Line Item
          </Button>
          <AddCatalogServiceButton tax={tax} onAdd={(item) => onChange([...items, item])} />
          <AddCatalogPartButton tax={tax} onAdd={(item) => onChange([...items, item])} />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label htmlFor="bulk-price" className="whitespace-nowrap text-muted-foreground">
              Whole service price{tax.enabled ? ` (${formatTaxSuffix(tax)})` : ''}
            </Label>
            <Input
              id="bulk-price"
              type="number"
              min={0}
              step={0.01}
              placeholder="0.00"
              value={bulkPrice}
              onChange={(e) => { setBulkPrice(e.target.value); setBulkError(''); }}
              className="h-9 w-28 text-right"
            />
            <Button type="button" variant="secondary" onClick={handleApplyBulkPrice} className="gap-2">
              <Calculator className="h-4 w-4" />
              Apply
            </Button>
          </div>
          {bulkError ? (
            <p className="text-sm text-destructive text-right">{bulkError}</p>
          ) : (
            <p className="text-xs text-muted-foreground text-right">
              No breakdown from the tech? Enter the total job price — it goes on the first line, other lines stay $0.00 (included).
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
